import { CourseId } from '../value-objects/CourseId';
import { LectureProgress } from '../value-objects/LectureProgress';
import { LearningProgressError } from '../shared/errors';

/**
 * 学習進捗の集約ルート
 */
export class LearningProgress {
  private constructor(
    private readonly id: string,
    private readonly courseId: CourseId,
    private lectureProgresses: Map<string, LectureProgress>,
    private examinationPassed: boolean,
    private readonly createdAt: Date,
    private updatedAt: Date,
    private passerToken?: string
  ) {}

  /**
   * 新しい学習進捗を作成
   * @param courseId - コースID
   * @param id - 学習進捗ID（省略時は自動生成）
   */
  static create(courseId: CourseId, id?: string): LearningProgress {
    const now = new Date();
    const progressId = id ?? LearningProgress.generateId(courseId);

    return new LearningProgress(
      progressId,
      courseId,
      new Map(),
      false, // examinationPassed初期値
      now,
      now,
      undefined // passerToken初期値
    );
  }

  /**
   * 学習進捗IDを生成
   * フォーマット: progress_{courseId}_{timestamp}
   */
  private static generateId(courseId: CourseId): string {
    return `progress_${courseId.toString()}_${Date.now()}`;
  }

  /**
   * 永続化データから学習進捗を復元
   */
  static reconstruct(data: {
    id: string;
    courseId: CourseId;
    lectureProgresses: LectureProgress[];
    examinationPassed: boolean;
    createdAt: Date;
    updatedAt: Date;
    passerToken?: string;
  }): LearningProgress {
    const progressMap = new Map<string, LectureProgress>();
    data.lectureProgresses.forEach(progress => {
      progressMap.set(progress.getLectureId(), progress);
    });

    return new LearningProgress(
      data.id,
      data.courseId,
      progressMap,
      data.examinationPassed,
      data.createdAt,
      data.updatedAt,
      data.passerToken
    );
  }

  /**
   * 講座を完了としてマーク
   * 動画の視聴が最後まで完了した時に呼び出す
   * @returns 新しいLearningProgressインスタンス（不変オブジェクトパターン）
   */
  completeLecture(lectureId: string): LearningProgress {
    let progress = this.lectureProgresses.get(lectureId);

    if (!progress) {
      // 講座が未登録の場合は新規作成してから完了にする
      progress = LectureProgress.create(lectureId);
    }

    // 完了状態にする
    const completed = progress.markAsCompleted();

    // 新しいMapを作成（イミュータブル）
    const newProgressMap = new Map(this.lectureProgresses);
    newProgressMap.set(lectureId, completed);

    // 新しいインスタンスを返す
    return new LearningProgress(
      this.id,
      this.courseId,
      newProgressMap,
      this.examinationPassed,
      this.createdAt,
      new Date(), // updatedAtを更新
      this.passerToken
    );
  }

  /**
   * 講座が完了しているか確認
   */
  isLectureCompleted(lectureId: string): boolean {
    const progress = this.lectureProgresses.get(lectureId);
    return progress ? progress.isCompleted() : false;
  }

  /**
   * 講座の進捗を取得
   */
  getLectureProgress(lectureId: string): LectureProgress | null {
    return this.lectureProgresses.get(lectureId) || null;
  }

  /**
   * すべての講座進捗を取得
   */
  getAllLectureProgresses(): LectureProgress[] {
    return Array.from(this.lectureProgresses.values());
  }

  /**
   * 完了した講座のIDリストを取得（後方互換性のため）
   */
  getCompletedLectures(): string[] {
    return Array.from(this.lectureProgresses.values())
      .filter(progress => progress.isCompleted())
      .map(progress => progress.getLectureId());
  }

  /**
   * 必須講座がすべて完了しているか判定
   */
  canTakeExamination(requiredLectureIds: string[]): boolean {
    return requiredLectureIds.every(id => this.isLectureCompleted(id));
  }

  /**
   * 修了テスト合格を記録
   * @param passerToken - 合格者トークン（省略可能）
   * @returns 新しいLearningProgressインスタンス（不変オブジェクトパターン）
   */
  markExaminationAsPassed(passerToken?: string): LearningProgress {
    return new LearningProgress(
      this.id,
      this.courseId,
      new Map(this.lectureProgresses),
      true, // examinationPassed
      this.createdAt,
      new Date(), // updatedAtを更新
      passerToken || this.passerToken
    );
  }

  /**
   * 修了テストに合格しているか判定
   */
  hasPassedExamination(): boolean {
    return this.examinationPassed;
  }

  /**
   * 修了テスト合格状態をリセット（期限切れなどの場合）
   * @returns 新しいLearningProgressインスタンス（不変オブジェクトパターン）
   */
  resetExaminationPassed(): LearningProgress {
    return new LearningProgress(
      this.id,
      this.courseId,
      new Map(this.lectureProgresses),
      false, // examinationPassed をリセット
      this.createdAt,
      new Date(), // updatedAtを更新
      undefined // passerToken を削除
    );
  }

  // ゲッター
  getId(): string {
    return this.id;
  }

  getCourseId(): CourseId {
    return this.courseId;
  }

  getCompletedLectureCount(): number {
    return this.getCompletedLectures().length;
  }

  getCreatedAt(): Date {
    return this.createdAt;
  }

  getUpdatedAt(): Date {
    return this.updatedAt;
  }

  getPasserToken(): string | undefined {
    return this.passerToken;
  }

  /**
   * 永続化用のデータに変換
   */
  toData(): {
    id: string;
    courseId: string;
    lectureProgresses: Array<{
      lectureId: string;
      completed: boolean;
      completedAt: string | null;
    }>;
    examinationPassed: boolean;
    createdAt: string;
    updatedAt: string;
    passerToken?: string;
  } {
    return {
      id: this.id,
      courseId: this.courseId.toString(),
      lectureProgresses: Array.from(this.lectureProgresses.values()).map(p =>
        p.toData()
      ),
      examinationPassed: this.examinationPassed,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
      passerToken: this.passerToken,
    };
  }
}
